import { CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME } from "models/generateCustomAttributesSchema";
import { BaseFormModeAdapter } from "./adapters";
import {
  FormConfiguration,
  SubmissionContext,
  FormInitParams,
} from "./definitions";
import { pluralize } from "utils/date-format";

/**
 * Adapter for Create mode
 * Handles form behavior for creating new Gaia objects
 */
export class CreateAdapter extends BaseFormModeAdapter {
  /**
   * Initialize form data for create mode
   */
  initializeFormData(params: FormInitParams): void {
    const {
      reset,
      setValue,
      defaultCountry,
      transformedGaiaObject,
      customAttributes,
    } = params;
    if (transformedGaiaObject && customAttributes) {
      const suffix = transformedGaiaObject.shortId
        ? `Copy of ${transformedGaiaObject.shortId}`
        : transformedGaiaObject.titleSuffix || "";

      reset({
        ...transformedGaiaObject,
        titleSuffix: suffix,
      });
      return;
    }
    if (transformedGaiaObject && !customAttributes) {
      return;
    }

    if (defaultCountry) {
      setValue(
        `${CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}.country`,
        defaultCountry
      );
    }
  }

  /**
   * Get form configuration for create mode
   */
  getFormConfig(): FormConfiguration {
    return {
      ...super.getFormConfig(),
      showTemplates: true,
      showFileUpload: true,
    };
  }

  getCancelRedirectPath(type: string, shortId?: string): string {
    return `/${pluralize(type)}`;
  }

  /**
   * Handle create submission
   * Flow: transform data → call createGaiaObject hook → optionally upload documents
   */
  async handleSubmit(data: any, context: SubmissionContext): Promise<void> {
    if (context.ui.setDisableCreateEdit) {
      context.ui.setDisableCreateEdit(true);
    }

    try {
      // 1. Transform data (using component function)
      const processedData = context.transforms.processGaiaObjectData(data);

      // 2. Create GaiaObject (hook handles API + notification + navigation)
      const response = await context.api.createGaiaObject(
        processedData,
        context
      );

      // 3. Upload all documents if files present
      if (
        context.formData.fileMetadata &&
        context.formData.fileMetadata.length > 0
      ) {
        // Upload all files with their individual document types
        for (const metadata of context.formData.fileMetadata) {
          await context.api.uploadDocument(
            metadata.file,
            response,
            context,
            metadata.documentType
          );
        }
      }
    } catch (error) {
      // Error handling is done in the hook
      throw error;
    }
  }
}
